import { Observable, combineLatest } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '~env/environment';
import { ApiConfig } from '~shared/constants/api.config';
import { HttpCrudService } from '~shared/services/http-crud.service';
import { ACCOUNT_INFORMATION_LINK } from '~shared/constants/links.constants';

import { UserInfo, UserInfoDB } from '../models/user-info.model';
import { IBusinessInfo, IPrivateInfo } from '../models/account-info.model';
import { AccountInfoUtil } from '../utils/account-info.util';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';

@Injectable()
export class AccountManagementHttpService extends HttpCrudService {
  public get userInfo$(): Observable<UserInfoDB> {
    return this.data$;
  }

  private apiEndpoint$ = this.accountInfoService.isEntrepreneur().pipe(
    map((isEntrepreneur) => {
      const apiEndpoint = isEntrepreneur ? '/entrepreneurs/me' : '/consumers/me';
      return ApiConfig.SVC_USER_ACCOUNT_API_URL().concat(apiEndpoint);
    }),
  );

  constructor(
    http: HttpClient,
    private accountInfoService: AccountInfoService,
  ) {
    super(http);
  }

  public getBusinessInfo(): Observable<IBusinessInfo> {
    return this.userInfo$.pipe(
      map((userInfo: UserInfoDB) => AccountInfoUtil.transformFromUserToBusinessInfo(userInfo)),
    );
  }

  public getPrivateInfo(): Observable<IPrivateInfo> {
    return this.userInfo$.pipe(
      map((userInfo: UserInfoDB) => AccountInfoUtil.transformFromUserToPrivateInfo(userInfo)),
    );
  }

  public read(): Observable<UserInfoDB> {
    return this.apiEndpoint$.pipe(
      switchMap((apiEndpoint) => this.request({ url: apiEndpoint })),
    );
  }

  public update(updatedInfo: UserInfo): Observable<UserInfoDB> {
    return combineLatest([this.accountInfoService.isEntrepreneur(), this.apiEndpoint$]).pipe(
      switchMap(([isEntrepreneur, apiEndpoint]) => {
        return this.request({
          url: apiEndpoint,
          reload: true,
          method: isEntrepreneur ? 'put' : 'patch',
          data: updatedInfo,
        });
      }),
    );
  }

  public changePassword(oldPassword: string, newPassword: string, newPasswordConfirmation: string): Observable<any> {
    return this.http.patch(`${ApiConfig.SVC_USER_ACCOUNT_API_URL()}/password-change`, {
      oldPassword,
      newPassword,
      newPasswordConfirmation,
    });
  }

  public changeEmail(newEmail: string, password: string): Observable<any> {
    const accountInfoUrl = `${environment.appBaseUrl()}${ACCOUNT_INFORMATION_LINK}`;
    return this.http.post(`${ApiConfig.SVC_IMS_API_URL()}/accounts/start-update-email`, {
      email: newEmail,
      password,
      linkExpirationUrl: `${accountInfoUrl}#expired_secret`,
      postUpdateRedirectUrl: accountInfoUrl,
      errorOnUpdateRedirectUrl: accountInfoUrl,
    });
  }
}
